import * as React from 'react'

import { useLazyQuery } from '@apollo/react-hooks'

import { useProductOptionsTransformer } from '@thg-commerce/enterprise-components'
import { QuickBuyModal } from '@thg-commerce/gravity-patterns'
import { ToastBannerType } from '@thg-commerce/gravity-elements'
import {
  i18n,
  EnterpriseContext,
  useLogger,
  useToastPresenter,
  useSiteConfig,
  useTransmit,
} from '@thg-commerce/enterprise-core'
import {
  Currency,
  Country,
  Scalars,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  VariantFields,
  Option,
} from '@thg-commerce/enterprise-components/src/transformer/productOptionsTransformer'
import { useBackendEventNormaliser } from '@thg-commerce/enterprise-metrics'

import { BasketContext } from '../BasketContext'
import { QuickBuyModal as QUICK_BUY_MODAL_QUERY } from './QuickBuyModal.graphql'
import { quickBuyModalDataTrackingCallbacks } from './dataTracking'
import { useAddToBasket } from '../hooks'

interface QuickBuyModalQueryVariables {
  sku: string
  currency: Currency
  shippingDestination: Country
  tagKeys: string[]
}

interface QuickBuyModalQueryData {
  quickBuyModal: {
    product: {
      sku: string
      title: string
      url: string
      defaultVariant: VariantFields
      images: {
        largeProduct: string
      }[]
      options: Option[]
      tags: string[]
      variants: VariantFields[]
    }
  }
}

export const QuickBuyModalPresenter = () => {
  const logger = useLogger()
  const { productTagsKeys } = useSiteConfig()
  const {
    headerHeight,
    currency,
    shippingDestination,
    extensionsRef,
  } = React.useContext(EnterpriseContext)
  const basketContext = React.useContext(BasketContext)
  const productOptionsTransformer = useProductOptionsTransformer()
  const transmit = useTransmit()
  const { normaliseBackendEvent, normaliserReady } = useBackendEventNormaliser(
    true,
  )

  const [open, setOpen] = React.useState(false)
  const [selectedVariant, setSelectedVariant] = React.useState<
    VariantFields | undefined
  >()
  const initialQuantity = React.useRef(1)
  const [showErrorMessage, setShowErrorMessage] = React.useState(false)

  const { execute: addToBasket } = useAddToBasket({
    errorHandler: () => setShowErrorMessage(true),
    forceAddToBasket: true,
  })
  const presentToast = useToastPresenter()
  const [loadData, { data, loading, error }] = useLazyQuery<
    QuickBuyModalQueryData,
    QuickBuyModalQueryVariables
  >(QUICK_BUY_MODAL_QUERY)

  const handleError = () => {
    presentToast({
      message: errorMessage,
      bannerType: ToastBannerType.DANGER,
    })
    setOpen(false)
    return null
  }

  React.useEffect(() => {
    if (error) {
      logger.error(
        `QuickBuyModalPresenter: Failed to load data for quick buy modal ${error}`,
      )
      handleError()
    }
  }, [error])

  React.useEffect(() => {
    if (data && open) {
      if (data.quickBuyModal.product.defaultVariant) {
        setSelectedVariant(data.quickBuyModal.product.defaultVariant)
      } else {
        setSelectedVariant(
          data.quickBuyModal.product.variants.find(
            (variant) => variant.inStock,
          ),
        )
      }
    }
  }, [data, open])

  React.useEffect(() => {
    if (!open) {
      setSelectedVariant(undefined)
    }
  }, [open])

  React.useEffect(() => {
    if (open && normaliserReady) {
      const {
        value: [getExtensions],
      } = extensionsRef
      const extensions = getExtensions()
      transmit({
        type: 'page_visit',
        payload: normaliseBackendEvent({
          eventData: {
            type: 'page_visit',
            subtype: 'quickbuy_modal_shown',
          },
          experiments: extensions?.experiments,
          requestData: {
            ...(extensions?.LoggerLinkData || {
              start_timestamp: 0,
              duration_ms: 0,
            }),
            url: window.location.href,
          },
        }),
      })
    }
  }, [open, normaliserReady])

  const variantChangedCallback = (variant: VariantFields) => {
    setSelectedVariant(variant)
  }

  const productOptions = React.useMemo(
    () =>
      productOptionsTransformer({
        selectedVariant,
        variantChangedCallback,
        options: data?.quickBuyModal.product.options || [],
        variants: data?.quickBuyModal.product.variants || [],
      }),
    [selectedVariant?.sku],
  )

  const i18nText = {
    modalTitle: i18n('basket.quickbuy.title'),
    rrpLabel: i18n('general.rrp.text'),
    saveLabel: i18n('general.save.text'),
    addToBasket: i18n('basket.addproduct.text'),
    viewMoreInformation: i18n('basket.productinfolink.text'),
    missingProductMessage: i18n('general.product.quickbuy.missing.text'),
  }

  const errorMessage = i18n('basket.addtobasket.error.text')

  const modalI18nText = {
    closeAriaLabel: i18n('general.modal.close.button.arialabel'),
    closeLabel: i18n('general.modal.close.button.label'),
  }

  const quantitySelectorI18nText = {
    decreaseButtonLabel: i18n('general.quantity.decrease'),
    increaseButtonLabel: i18n('general.quantity.increase'),
    quantityLabel: i18n('general.quantity.text'),
  }

  const isFromRecommendations = React.useRef<boolean>(false)

  if (!basketContext.presentQuickBuyModal) {
    logger.warn(
      'QuickBuyModalPresenter: Failed to find an initialised BasketContext. Make sure presentQuickBuyModal ref is defined and it wraps QuickBuyModalPresenter.',
    )
    return null
  }

  basketContext.presentQuickBuyModal.current = (
    sku: string,
    quantity: number,
    fromRecommendations: boolean = false,
  ) => {
    initialQuantity.current = quantity

    isFromRecommendations.current = fromRecommendations

    loadData({
      variables: {
        sku: sku as Scalars['SKU'],
        currency: currency as Currency,
        shippingDestination: shippingDestination.code as Country,
        tagKeys: productTagsKeys || [],
      },
    })
    setOpen(true)
  }

  if (!open) {
    return null
  }

  if (
    !loading &&
    !data?.quickBuyModal.product.variants.find((variant) => variant.inStock)
  ) {
    logger.error('QuickBuyModalPresenter: Failed to pick a variant to render')
    return handleError()
  }

  const addToBasketCallback = (
    _: { [option: string]: string },
    quantity: number,
  ) => {
    if (!basketContext.presentAddedToBasketModal) {
      return
    }

    if (!selectedVariant) {
      return
    }

    const currentAddedToBasketModalPresenter =
      basketContext.presentAddedToBasketModal.current
    basketContext.presentAddedToBasketModal.current = (
      sku: string,
      quantity: number,
    ) => {
      if (!basketContext.presentAddedToBasketModal) {
        return
      }

      setOpen(false)
      currentAddedToBasketModalPresenter(sku, quantity)

      basketContext.presentAddedToBasketModal.current = currentAddedToBasketModalPresenter
    }

    addToBasket(
      [{ quantity, sku: selectedVariant.sku.toString() }],
      isFromRecommendations.current,
    )
    quickBuyModalDataTrackingCallbacks.modalAddToBasket()
  }

  return (
    <QuickBuyModal
      loading={loading}
      rendering={{
        open,
        onClose: () => {
          setOpen(false)
          quickBuyModalDataTrackingCallbacks.modalClose()
        },
      }}
      i18nText={{
        ...i18nText,
        failedToAddToBasketError: showErrorMessage ? errorMessage : undefined,
        availabilityMessage: selectedVariant?.availabilityMessage,
      }}
      modal={{
        i18nText: modalI18nText,
        showHeader: true,
        stickyHeader: true,
        headerOffset: headerHeight,
        gridColSpan: [12, 8, 8, 6],
        'data-testid': 'quick-buy-modal',
      }}
      productOptions={productOptions}
      product={
        (data &&
          selectedVariant && {
            sku: data.quickBuyModal.product.sku,
            image: {
              urls: {
                largeProduct: selectedVariant.images[0].largeProduct!,
              },
              alt: selectedVariant.title,
            },
            title: selectedVariant.title,
            tags: data.quickBuyModal.product.tags,
            inStock: selectedVariant.inStock,
          }) ||
        undefined
      }
      pricing={
        (selectedVariant &&
          selectedVariant.price?.rrp && {
            price: selectedVariant.price.price.displayValue,
            rrp: selectedVariant.price.rrp.displayValue,
          }) ||
        undefined
      }
      links={
        (data && {
          productUrl: data.quickBuyModal.product.url,
        }) ||
        undefined
      }
      callbacks={{
        addedToBasketClicked: addToBasketCallback,
        viewMoreInformationClicked: () => {
          data &&
            quickBuyModalDataTrackingCallbacks.modalInformation(
              data.quickBuyModal.product.sku,
            )
        },
      }}
      quantitySelector={{
        i18nText: quantitySelectorI18nText,
        maxValue: selectedVariant?.maxPerOrder || 99,
        quantity: initialQuantity.current,
      }}
    />
  )
}
